import type { MouseEvent, ReactElement } from 'react';
import { Fragment, useEffect, useState } from 'react';

import { useSelectedDentistDataStore } from '../../../../stores/usePaginacao';
import { ChevronRight } from '../../../icons/ChevronRight';
import { MapIcon } from '../../../icons/MapIcon';
import { PhoneIcon } from '../../../icons/PhoneIcon';
import styles from './docs.module.css';

interface Props {
  docList: any[];
  loading?: boolean;
  className?: string;
}

const toUpperText = (value: any) =>
  typeof value === 'string' ? value.toLocaleUpperCase('pt-BR') : value || '';

const formatTelefone = (telefone: any) => {
  if (Array.isArray(telefone)) {
    return telefone
      .map(tel => tel.telefone || tel.descricao || tel.descricaoContato)
      .filter(Boolean)
      .join(' / ');
  }

  return telefone || '';
};

const getClinicDomId = (id: number | string) =>
  'dentist-card-' + String(id).replace(/[^a-zA-Z0-9_-]/g, '');

const DocList = ({
  docList,
  loading = false,
  className = '',
}: Props): ReactElement => {
  const [openProducts, setOpenProducts] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState<any>(null);
  const [produtos, setProdutos] = useState<any[]>([]);
  const selectedDentist = useSelectedDentistDataStore(x => x.data);
  const setSelectedDentist = useSelectedDentistDataStore(
    x => x.setselectedDentistData,
  );

  const openWaze = (doc: any, event?: MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if (!doc.latitude || !doc.longitude) return;
    window.open(
      'https://waze.com/ul?ll=' +
        doc.latitude +
        ',' +
        doc.longitude +
        '&navigate=yes',
      '_blank',
    );
  };

  const openUber = (doc: any, event?: MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if (!doc.latitude || !doc.longitude) return;
    window.open(
      'https://m.uber.com/ul/?action=setPickup&dropoff[latitude]=' +
        doc.latitude +
        '&dropoff[longitude]=' +
        doc.longitude,
      '_blank',
    );
  };

  useEffect(() => {
    if (!selectedDentist?.clinicaId) return;

    document
      .getElementById(getClinicDomId(selectedDentist.clinicaId))
      ?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
  }, [selectedDentist]);

  useEffect(() => {
    if (!openProducts || produtos.length > 0) return;

    const urlBase = process.env.REACT_APP_URL_BASE;
    const token = process.env.REACT_APP_TOKEN_S4E_V2;
    if (!urlBase || !token) return;

    const apiUrl = urlBase.endsWith('/')
      ? urlBase + 'api/ClassificacaoAns/Buscar?token=' + token
      : urlBase + '/api/ClassificacaoAns/Buscar?token=' + token;

    fetch(apiUrl)
      .then(response => response.json())
      .then(data => setProdutos(Array.isArray(data.dados) ? data.dados : []))
      .catch(() => setProdutos([]));
  }, [openProducts, produtos.length]);

  const selectClinic = (doc: any) => {
    setSelectedDentist(doc);
  };

  return (
    <>
      <aside className={[styles.docPanel, className].filter(Boolean).join(' ')}>
        <div className={styles.docHeader}>
          <div>
            <p className="text-xs font-bold uppercase text-primary-main">
              Cl&iacute;nicas e dentistas
            </p>
            <p className="mt-1 text-sm text-medium-emphasis">
              {loading
                ? 'Buscando unidades na rede...'
                : docList.length > 0
                ? 'Clique em uma unidade para focar no mapa.'
                : 'A lista sera preenchida apos a busca.'}
            </p>
          </div>
          <span className="rounded-full bg-primary-container px-3 py-1 text-sm font-bold text-primary-main">
            {loading ? '...' : docList.length}
          </span>
        </div>

        <div className={styles.docListContent}>
          {loading ? (
            <div
              className={styles.loadingStack}
              aria-label="Carregando resultados"
            >
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className={styles.loadingItem}
                  style={{ animationDelay: String(index * 90) + 'ms' }}
                />
              ))}
            </div>
          ) : docList.length === 0 ? (
            <div className={styles.emptyState}>
              <img
                src="/assets/images/icons/search.svg"
                alt=""
                className="mb-4 h-12 w-12 opacity-70"
              />
              <h3 className="text-lg font-semibold text-primary-main">
                Nenhuma unidade listada
              </h3>
              <p className="mt-2 text-sm text-medium-emphasis">
                Ajuste os filtros ou tente uma regi&atilde;o pr&oacute;xima para
                encontrar mais op&ccedil;&otilde;es.
              </p>
            </div>
          ) : (
            <ul className={styles.docList}>
              {docList.map((doc, index) => {
                const clinicId = doc.clinicaId || doc.idFuncionario || index;
                const selected = Boolean(
                  selectedDentist?.clinicaId &&
                    doc.clinicaId &&
                    String(selectedDentist.clinicaId) === String(doc.clinicaId),
                );
                const location =
                  doc.uf || doc.municipio
                    ? ' - ' +
                      toUpperText(doc.uf) +
                      ' ' +
                      toUpperText(doc.municipio)
                    : '';

                return (
                  <li
                    id={getClinicDomId(clinicId)}
                    className={[
                      styles.item,
                      selected ? styles.selectedItem : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    key={clinicId}
                    onClick={() => selectClinic(doc)}
                    style={{
                      animationDelay: String(Math.min(index, 8) * 45) + 'ms',
                    }}
                  >
                    <p className={styles.docInfo}>
                      {toUpperText(doc.tipoEstabelecimento || 'CLINICA')}
                    </p>
                    <div className={styles.docData}>
                      <div className={styles.docMainData}>
                        <div>
                          <h3 className={styles.docTitle}>
                            {toUpperText(doc.nomeFantasia || doc.razaoSocial)}
                          </h3>

                          <div className="flex items-start gap-3 py-3">
                            <MapIcon />
                            <div className="text-sm text-medium-emphasis">
                              <p>{toUpperText(doc.endereco) + location}</p>
                            </div>
                          </div>

                          {formatTelefone(doc.telefone) && (
                            <div className="flex items-center gap-3">
                              <PhoneIcon />
                              <p className="text-sm text-medium-emphasis">
                                {toUpperText(formatTelefone(doc.telefone))}
                              </p>
                            </div>
                          )}
                        </div>

                        <button
                          aria-label="Focar unidade no mapa"
                          className={styles.focusButton}
                          onClick={event => {
                            event.stopPropagation();
                            selectClinic(doc);
                          }}
                        >
                          <ChevronRight />
                        </button>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          className="rounded-lg bg-primary-container px-3 py-2 text-xs font-semibold uppercase text-primary-main transition hover:bg-primary-light hover:text-white"
                          onClick={event => {
                            event.stopPropagation();
                            setSelectedClinic(doc);
                            setOpenProducts(true);
                          }}
                        >
                          Produtos atendidos
                        </button>
                        <button
                          className="flex h-10 items-center gap-2 rounded-lg border border-neutral-lighter px-3 text-xs font-semibold uppercase text-medium-emphasis transition hover:border-primary-main hover:text-primary-main"
                          title="Abrir no Waze"
                          onClick={event => openWaze(doc, event)}
                        >
                          <img
                            src="/assets/images/waze-logo.svg"
                            alt=""
                            className="h-5 w-5"
                          />
                          <span>Waze</span>
                        </button>
                        <button
                          className="flex h-10 items-center gap-2 rounded-lg border border-neutral-lighter px-3 text-xs font-semibold uppercase text-medium-emphasis transition hover:border-primary-main hover:text-primary-main"
                          title="Abrir no Uber"
                          onClick={event => openUber(doc, event)}
                        >
                          <img
                            src="/assets/images/uber-logo.svg"
                            alt=""
                            className="h-5 w-5"
                          />
                          <span>Uber</span>
                        </button>
                      </div>

                      {Array.isArray(doc.dentistas) &&
                        doc.dentistas.length > 0 && (
                          <div className="mt-4 rounded-lg bg-neutral-white p-3">
                            <p className={styles.docInfo}>DENTISTAS</p>
                            {doc.dentistas.map(dent => (
                              <Fragment key={dent.id || String(dent.nome)}>
                                <h3 className={styles.docTitle}>
                                  {toUpperText(dent.nome)}
                                </h3>
                                <div className="flex items-center gap-4 py-2">
                                  <p className={styles.docInfo}>
                                    {toUpperText(dent.especialidades)}
                                  </p>
                                </div>
                                {Array.isArray(dent.qualiss) &&
                                  dent.qualiss.length > 0 && (
                                    <div className="flex flex-wrap gap-2 py-2">
                                      {dent.qualiss.map((qual, qualIndex) => (
                                        <span
                                          key={
                                            String(dent.id) + '-' + qualIndex
                                          }
                                          className="rounded-full bg-primary-container px-2 py-1 text-xs uppercase text-primary-main"
                                        >
                                          {toUpperText(
                                            qual.nomeEspecialidade ||
                                              qual.Descricao,
                                          )}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                              </Fragment>
                            ))}
                          </div>
                        )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>

      {openProducts && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="flex max-h-[80vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-card">
            <div className="flex items-center justify-between bg-primary-main px-4 py-3 text-white">
              <h2 className="text-lg font-semibold">PRODUTOS ATENDIDOS</h2>
              <button
                className="text-xl font-bold"
                onClick={() => setOpenProducts(false)}
              >
                X
              </button>
            </div>
            <div className="overflow-auto p-4">
              <p className="mb-3 text-sm text-medium-emphasis">
                <strong>CLINICA:</strong>{' '}
                {toUpperText(selectedClinic?.nomeFantasia)}
              </p>
              <table className="w-full border-collapse border border-neutral-lighter text-sm">
                <thead className="bg-neutral-surface">
                  <tr>
                    <th className="border border-neutral-lighter p-2 text-left">
                      REGISTRO
                    </th>
                    <th className="border border-neutral-lighter p-2 text-left">
                      NOME
                    </th>
                    <th className="border border-neutral-lighter p-2 text-left">
                      SEGMENTACAO
                    </th>
                    <th className="border border-neutral-lighter p-2 text-left">
                      SITUACAO
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {produtos.map(item => (
                    <tr key={item.idClassificacao || item.codigoAns}>
                      <td className="border border-neutral-lighter p-2">
                        {toUpperText(item.codigoAns)}
                      </td>
                      <td className="border border-neutral-lighter p-2">
                        {toUpperText(item.descricao)}
                      </td>
                      <td className="border border-neutral-lighter p-2">
                        ODONTOLOGICA
                      </td>
                      <td className="border border-neutral-lighter p-2">
                        {item.status === 1 ? 'ATIVO' : 'INATIVO'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DocList;
